import React, { createContext, useEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useI18n } from 'next-localization';

import { ParsedUrlQuery } from 'querystring';

import { getAppliedFiltersForBreadcrumbs } from '@/features/listing/utils';
import { INITIAL_HOST_TITLE } from '@/lib/constants';
import { getBreadcrumbsFromPath } from '@/lib/get-breadcrumbs';
import { getDshs } from '@/lib/get-dshs';
import mapPageInformation from '@/lib/map-page-information';
import { SitecorePageProps } from '@/lib/page-props';
import { isHospitalSiteType, isLocationMarketContext } from '@/lib/utils';
import {
  GlobalAppState,
  HostApp,
  OasAppContextProps,
  OasAppProviderProps,
  OASSitecoreComponentFields,
  PageState,
  Trans,
} from '@/types/OasAppContextTypes';
import { ComponentFields, ComponentRendering, Field } from '@sitecore-jss/sitecore-jss-nextjs';

const OasAppContext = createContext<
  | (OasAppContextProps & {
      updateAppState: (newState: Partial<GlobalAppState>) => void;
    })
  | undefined
>(undefined);

export const OasAppProvider: React.FC<OasAppProviderProps> = ({ pageProps, query, children }) => {
  const pathname = usePathname();
  const bookingIdFromPath = pathname.split('/confirmation/')[1]?.split('/')[0];
  const { t } = useI18n();

  const isLocation = pageProps?.originalPath?.includes('/locations') || false;

  // Compute initial global state ONCE (first render) -- doesn't include page-specific fields
  const [globalState, setGlobalState] = useState<GlobalAppState>(() => {
    const initial = deriveAppState(pageProps, query, pathname, t);
    // Remove page-specific fields
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { pageTitle, pageShortDescription, breadcrumbs, ...rest } = initial;

    if (
      typeof window !== 'undefined' &&
      initial.dotcms &&
      !sessionStorage.getItem(INITIAL_HOST_TITLE)
    ) {
      sessionStorage.setItem(INITIAL_HOST_TITLE, initial.hostTitle);
    }
    return rest;
  });

  // Memoize page-specific state, changes on navigation/props
  // Include window.location.search to track filter changes in breadcrumbs
  const searchParams = typeof window !== 'undefined' ? window.location.search : '';
  const pageState = useMemo<PageState>(() => {
    const derived = deriveAppState(pageProps, query, pathname, t);
    return {
      pageTitle: derived.pageTitle,
      pageShortDescription: derived.pageShortDescription,
      bookingId: bookingIdFromPath,
      breadcrumbs: derived.breadcrumbs,
      bookingDsEnglish: derived.bookingDsEnglish,
      bookingDsSpanish: derived.bookingDsSpanish,
      isLocation: derived.isLocation || isLocation,
      scSiteCookie: derived.scSiteCookie,
    };
    // searchParams is needed to trigger re-computation when URL query changes (filters applied)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageProps, query, pathname, bookingIdFromPath, t, isLocation, searchParams]);

  // Allow updates to global state (e.g. DSHS agreement, host info)
  const updateAppState = (newState: Partial<GlobalAppState>) => {
    setGlobalState((prev) => ({ ...prev, ...newState }));
  };

  // Merge for context consumers
  const contextValue = useMemo(
    () => ({
      ...globalState,
      ...pageState,
      updateAppState,
    }),
    [globalState, pageState]
  );

  // DSHS agreement fetch logic (runs on mount or when relevant fields change)
  useEffect(() => {
    // Only in non-sitecore mode, dotcms state, and when DSHS is missing
    if (
      globalState.hostApp !== 'sitecore' &&
      globalState.dotcms &&
      isHospitalSiteType(globalState.siteType) &&
      (!globalState.dshsEnglish || !globalState.dshsSpanish)
    ) {
      const { dotCmsHostId, dotCmsHostName, mbcDefaultAccesspoint } = globalState.dotcms;
      getDshs({ dotCmsHostId, dotCmsHostName, mbcDefaultAccesspoint }).then(
        ({ dshsEnglish, dshsSpanish }) => {
          if (dshsEnglish || dshsSpanish) {
            setGlobalState((prev) => ({
              ...prev,
              dshsEnglish,
              dshsSpanish,
            }));
          }
        }
      );
    }
  }, [
    globalState.hostApp,
    globalState.dotcms,
    globalState.siteType,
    globalState.dshsEnglish,
    globalState.dshsSpanish,
  ]);

  return <OasAppContext.Provider value={contextValue}>{children}</OasAppContext.Provider>;
};

export const useOasApp = (): OasAppContextProps => {
  const context = React.useContext(OasAppContext);
  if (!context) {
    throw new Error('useOasApp must be used within an OasAppProvider');
  }
  return context;
};

// === Helpers and Logic (original) ===

function deriveAppState(
  pageProps: SitecorePageProps,
  query: ParsedUrlQuery,
  pathname: string,
  t: Trans
): OasAppContextProps {
  const standaloneState = getStandaloneState(query, pathname, pageProps, t);
  const derivedState =
    standaloneState.hostApp !== 'sitecore'
      ? standaloneState
      : getSitecoreState(pageProps, pathname, t);

  derivedState.pageTitle = getPageTitle(derivedState as OasAppContextProps, pathname, t);

  return derivedState as OasAppContextProps;
}

const getSitecoreState = (
  pageProps: SitecorePageProps,
  pathname: string,
  t: Trans
): OasAppContextProps => {
  const props = getSitecoreComponentProps(pageProps, t) || {};
  const { coids, hostTitle, siteType, program, marketContext, notFound, ...sitecoreProps } = props;
  const isLocation = marketContext ? isLocationMarketContext(marketContext) : false;

  const bookingIdFromPath = pathname.split('/confirmation/')[1]?.split('/')[0];
  const doctorProfileFromBookingId =
    typeof window !== 'undefined' && bookingIdFromPath
      ? sessionStorage.getItem(bookingIdFromPath)
      : null;

  const appState = {
    hostApp: HostApp.sitecore,
    ...sitecoreProps,
    hostTitle: hostTitle || '',
    siteType: siteType || 'Hospital Operations',
    coids: coids || '',
    isLocation: isLocation,
    program: program || 'web',
    notFound: !!notFound,
    searchAddressData: pageProps.searchAddressData,
    specialties: pageProps.specialties || [],
    scSiteCookie: pageProps.scSiteCookie,
    currentBookingInformation: {
      bookingId: bookingIdFromPath,
      doctorUrlTitle: doctorProfileFromBookingId,
    },
  };

  const appliedFilters = getAppliedFiltersForBreadcrumbs();
  const breadcrumbs = getBreadcrumbsFromPath(t, appState, pathname, appliedFilters);

  return {
    ...appState,
    breadcrumbs,
  };
};

type StandaloneReturn = Omit<OasAppContextProps, 'hostApp'> & {
  hostApp: Omit<HostApp, 'sitecore'>;
};
type SitecoreStandaloneReturn = Partial<Omit<OasAppContextProps, 'hostApp'>> & {
  hostApp: 'sitecore';
};

const getStandaloneState = (
  params: ParsedUrlQuery,
  pathname: string,
  pageProps: SitecorePageProps,
  t: Trans
): StandaloneReturn | SitecoreStandaloneReturn => {
  const hostApp = (params.hostApp ? (params.hostApp as string).toLowerCase() : undefined) as
    | string
    | undefined;

  if (!hostApp || !(hostApp in HostApp)) {
    return {
      hostApp: HostApp.sitecore,
    };
  }

  const isMho = hostApp === HostApp.mho || hostApp === HostApp.mhonative;

  const { notFound, ...sitecoreData } = getSitecoreComponentProps(pageProps, t) || {};

  const coids = (params.facilityCoids || '') as string;

  let siteType = (params.siteType || 'Hospital Operations') as string;
  if (siteType.toLowerCase() === 'hospital') {
    siteType = 'Hospital Operations';
  }

  const program = (params.program || 'web') as string;
  const hostTitle = (params.hostTitle || '') as string;

  const dotCmsHostId = isMho
    ? (process.env.MHO_DOTCMS_HOST_ID as string)
    : ((params.dotcmsHostId || '') as string);
  const dotCmsHostName = isMho
    ? (process.env.MHO_DOTCMS_HOST_NAME as string)
    : ((params.dotcmsHostName || '') as string);

  const coreHostId = params.coreHostId as string | undefined;

  const mbcLocationAccesspoint = (params.mbcLocationAccesspoint || '') as string;
  const mbcDefaultAccesspoint = (params.mbcDefaultAccesspoint || '') as string;

  const specialties = pageProps.specialties || [];

  const isLocation = pageProps?.originalPath?.includes('/locations') || false;
  const scSiteCookie = pageProps.scSiteCookie;

  const appState = {
    hostApp: HostApp[hostApp as keyof typeof HostApp],
    ...sitecoreData,
    notFound: !!notFound,
    siteType,
    coids,
    program,
    hostTitle,
    isLocation,
    specialties,
    scSiteCookie,
    currentBookingInformation: { doctorUrlTitle: null },
    dotcms: {
      dotCmsHostId,
      dotCmsHostName,
      coreHostId,
      mbcLocationAccesspoint,
      mbcDefaultAccesspoint,
    },
  };

  const appliedFilters = getAppliedFiltersForBreadcrumbs();
  const breadcrumbs = getBreadcrumbsFromPath(t, appState, pathname, appliedFilters);

  return {
    ...appState,
    hostApp: hostApp as Omit<HostApp, 'sitecore'>,
    breadcrumbs,
  };
};

const getSitecoreComponentProps = (
  pageProps: SitecorePageProps,
  t: Trans
): OASSitecoreComponentFields | undefined => {
  const routeData = pageProps?.layoutData?.sitecore?.route;
  if (!routeData) {
    console.error('No route data found in pageProps');
    return undefined;
  }
  const routeBody = routeData?.placeholders?.body?.[0] as
    | ComponentRendering<ComponentFields>
    | undefined;
  const pageData = routeBody?.placeholders?.['col-1']?.[0] as
    | ComponentRendering<OASSitecoreComponentFields>
    | undefined;
  const pageDataFields = pageData?.fields;

  if (!pageDataFields) {
    console.error('No page data fields found in route body');
    return undefined;
  }

  const { pageTitle: pageTitleFromFields, ...restPageDataFields } = pageDataFields;

  const pageTitle = pageTitleFromFields
    ? typeof pageTitleFromFields === 'string'
      ? { value: pageTitleFromFields }
      : pageTitleFromFields
    : undefined;
  const referralPhone = pageDataFields.physicianReferralPhoneNumberEnglish;
  const phoneLink = `<a href="tel:${referralPhone}" class="d-inline d-lg-none">${referralPhone}</a><span class="d-none d-lg-inline">${referralPhone}</span>`;
  const pageSubtitle = referralPhone
    ? `${t('OAS.LandingPage.Subtitle')} ${t('OAS.LandingPage.PhysicianReferralPhoneNumber', { phone: phoneLink })}`
    : t('OAS.LandingPage.Subtitle');
  const showPageSubtitle =
    (routeData.name !== 'listing' || isHospitalSiteType(pageDataFields?.siteType || '')) &&
    pageSubtitle;

  return {
    ...restPageDataFields,
    pageShortDescription: {
      value:
        pageDataFields?.pageShortDescription?.value || (showPageSubtitle && pageSubtitle) || '',
    },
    pageTitle,
  };
};

const getPageTitle = (
  state: OasAppContextProps,
  pathname: string,
  t: (key: string) => string
): Field<string> | undefined => {
  const title = state.pageTitle || state.hostTitle;

  if (title) return typeof title === 'string' ? { value: title } : title;

  const splitPath = pathname.split('/').filter((p) => p);

  const baseRoute = splitPath.length === 1 ? splitPath[0] : splitPath[1];
  const pageTitleItem = mapPageInformation(t, state)[
    baseRoute as keyof ReturnType<typeof mapPageInformation>
  ];

  if (pageTitleItem && pageTitleItem.title) {
    return { value: pageTitleItem.title };
  }

  return undefined;
};
